// Shared readers for presenting a person's full name and their declared
// medical conditions.
//
// Partner journeys write these details in several different shapes: flat
// fields on the dependant, a nested additional_details object, or a single
// policy/quotation level list covering every member. The readers below look
// in each known location instead of assuming one, so a screen shows what was
// captured rather than a blank.

// Ordered so the assembled name reads naturally. Duplicates across the
// aliases (last_name === surname) are dropped while joining.
const NAME_PARTS = [
  'title',
  'first_name',
  'given_name',
  'given_names',
  'middle_name',
  'middle_names',
  'other_names',
  'last_name',
  'surname'
];

// The title is useful on a detail card but must never be the only thing a
// name is built from.
const NAME_PARTS_WITHOUT_TITLE = NAME_PARTS.filter(
  (field) => field !== 'title'
);

const FULL_NAME_FIELDS = ['full_name', 'fullName', 'name', 'full_names'];

const CONDITION_FIELDS = [
  'medical_conditions',
  'medical_condition',
  'declared_medical_conditions',
  'declared_medical_condition',
  'pre_existing_conditions',
  'pre_existing_condition',
  'preexisting_conditions',
  'preexisting_condition',
  'medical_pre_conditions',
  'medical_pre_condition',
  'health_conditions',
  'health_condition',
  'chronic_conditions',
  'chronic_illnesses',
  'conditions'
];

// Fields that hold the condition's name when a condition is an object.
const CONDITION_NAME_FIELDS = [
  'condition',
  'condition_name',
  'name',
  'description',
  'label',
  'value',
  'type',
  'title'
];

// Values that mean "nothing was declared" rather than a named condition.
const EMPTY_CONDITION_VALUES = [
  'none',
  'no',
  'n/a',
  'na',
  'nil',
  'null',
  'false',
  'true',
  'yes',
  'nothing',
  'none declared',
  'no medical condition',
  'no medical conditions',
  'no pre existing condition',
  'no pre existing conditions'
];

// Keys whose value is a list of members, each of which may carry its own
// declared conditions.
const MEMBER_LIST_FIELDS = [
  'members',
  'dependants',
  'dependents',
  'lives',
  'family_members',
  'insured_members',
  'medical_conditions',
  'members_with_preexisting_conditions',
  'members_with_pre_existing_conditions',
  'members_with_medical_conditions'
];

// Lists that only ever contain members who DID declare something. Being named
// in one of these is itself the declaration, even when no condition is spelled
// out. Membership of a plain "members"/"dependants" list means nothing.
const CONDITION_MEMBER_LIST_FIELDS = [
  'members_with_preexisting_conditions',
  'members_with_pre_existing_conditions',
  'members_with_medical_conditions',
  'medical_conditions'
];

// Yes/no answers to "any pre-existing condition?". They record THAT something
// was declared without naming it.
const CONDITION_FLAG_FIELDS = [
  'pre_existing_condition',
  'pre_existing_conditions',
  'preexisting_condition',
  'preexisting_conditions',
  'medical_pre_condition',
  'medical_pre_conditions',
  'has_medical_condition',
  'has_medical_conditions',
  'has_pre_existing_condition',
  'medical_condition',
  'medical_conditions'
];

// Keys that can identify which member a record belongs to.
const MEMBER_ID_FIELDS = [
  'national_id',
  'id_number',
  'identification_number',
  'passport',
  'passport_number',
  'guid',
  'dependant_guid'
];

/**
 * Turn a stored condition key into something readable, e.g.
 * "highBloodPressure" or "high_blood_pressure" -> "High Blood Pressure".
 */
export function toReadableCondition(value) {
  if (typeof value !== 'string') {
    return '';
  }
  // A stored key ("heart_disease", "highBloodPressure") reads best title
  // cased. Free text a user typed is left as written apart from its first
  // letter, so wording and capitalisation they chose survive.
  const isStoredKey = !/\s/.test(value.trim());
  const spaced = value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) {
    return '';
  }
  if (isStoredKey) {
    return spaced
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function splitIntoWords(value) {
  if (typeof value !== 'string') {
    return [];
  }
  return value.trim().split(/\s+/).filter((word) => word.length > 0);
}

function joinUniqueWords(words) {
  const seen = {};
  const kept = [];
  words.forEach((word) => {
    const key = word.toLowerCase();
    if (seen[key]) {
      return;
    }
    seen[key] = true;
    kept.push(word);
  });
  return kept.join(' ');
}

// A name part can be blank on the record itself and present in its
// additional_details — OMK dependants carry middle_name only there — so both
// are consulted before a part is treated as missing.
function readNamePart(person, field) {
  const direct = person[field];
  if (typeof direct === 'string' && direct.trim()) {
    return direct;
  }
  const extra = person.additional_details;
  if (extra && typeof extra === 'object') {
    const nested = extra[field];
    if (typeof nested === 'string' && nested.trim()) {
      return nested;
    }
  }
  return '';
}

function collectNameParts(person, fields) {
  const words = [];
  fields.forEach((field) => {
    splitIntoWords(readNamePart(person, field)).forEach((word) => {
      words.push(word);
    });
  });
  return words;
}

function readFullNameField(person) {
  for (let i = 0; i < FULL_NAME_FIELDS.length; i += 1) {
    const value = person[FULL_NAME_FIELDS[i]];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

/**
 * Every name a person has, in reading order.
 *
 * Screens used to render `first_name + " " + last_name`, which silently drops
 * middle and other names. This assembles all the name fields present and,
 * when a stored full_name carries more names than the individual fields do,
 * prefers the fuller version.
 */
export function getFullName(person, options) {
  if (!person || typeof person !== 'object') {
    return '';
  }
  const includeTitle = !!(options && options.includeTitle);
  const fields = includeTitle ? NAME_PARTS : NAME_PARTS_WITHOUT_TITLE;
  const partWords = collectNameParts(person, fields);
  const fullNameWords = splitIntoWords(readFullNameField(person));

  if (fullNameWords.length > partWords.length) {
    return joinUniqueWords(fullNameWords);
  }
  if (partWords.length) {
    return joinUniqueWords(partWords);
  }
  return joinUniqueWords(fullNameWords);
}

/**
 * getFullName with a caller supplied placeholder for the empty case.
 */
export function getFullNameOr(person, placeholder) {
  const name = getFullName(person);
  return name || (placeholder === undefined ? 'N/A' : placeholder);
}

function isEmptyConditionValue(text) {
  const normalised = text.trim().toLowerCase();
  if (!normalised) {
    return true;
  }
  return EMPTY_CONDITION_VALUES.indexOf(normalised) !== -1;
}

function pushCondition(text, list, seen) {
  const readable = toReadableCondition(text);
  if (!readable || isEmptyConditionValue(readable)) {
    return;
  }
  const key = readable.toLowerCase();
  if (seen[key]) {
    return;
  }
  seen[key] = true;
  list.push(readable);
}

function firstNamedField(value) {
  for (let i = 0; i < CONDITION_NAME_FIELDS.length; i += 1) {
    const candidate = value[CONDITION_NAME_FIELDS[i]];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function collectFromValue(value, list, seen) {
  if (value === null || value === undefined) {
    return;
  }
  if (typeof value === 'string') {
    // Checked whole first, so a placeholder such as "N/A" is discarded rather
    // than split into fragments.
    if (isEmptyConditionValue(value)) {
      return;
    }
    value.split(/[,;|]/).forEach((part) => pushCondition(part, list, seen));
    return;
  }
  // A bare true/false or a count carries no condition name of its own.
  if (typeof value === 'boolean' || typeof value === 'number') {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectFromValue(item, list, seen));
    return;
  }
  if (typeof value !== 'object') {
    return;
  }

  const named = firstNamedField(value);
  if (named) {
    collectFromValue(named, list, seen);
    return;
  }

  // Checkbox style capture: { diabetes: true, asthma: false }.
  Object.keys(value).forEach((key) => {
    const entry = value[key];
    if (entry === true) {
      pushCondition(key, list, seen);
      return;
    }
    if (typeof entry === 'string') {
      collectFromValue(entry, list, seen);
    }
  });
}

function mergeConditions(sources) {
  const list = [];
  const seen = {};
  sources.forEach((source) => {
    if (!source || typeof source !== 'object') {
      return;
    }
    CONDITION_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(source, field)) {
        collectFromValue(source[field], list, seen);
      }
    });
  });
  return list;
}

/**
 * Named medical conditions declared directly on a record.
 */
export function getMedicalConditions(source) {
  return mergeConditions([source]);
}

/**
 * Named medical conditions for a person, including anything nested under
 * their additional_details.
 */
export function getPersonMedicalConditions(person) {
  if (!person || typeof person !== 'object') {
    return [];
  }
  return mergeConditions([
    person,
    person.additional_details,
    person.details,
    person.medical_details
  ]);
}

function normaliseForMatch(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function isSamePerson(record, person) {
  if (!record || !person) {
    return false;
  }
  for (let i = 0; i < MEMBER_ID_FIELDS.length; i += 1) {
    const field = MEMBER_ID_FIELDS[i];
    const left = record[field];
    const right = person[field];
    if (left && right && String(left) === String(right)) {
      return true;
    }
  }
  const recordName = normaliseForMatch(getFullName(record));
  const personName = normaliseForMatch(getFullName(person));
  return !!recordName && recordName === personName;
}

function candidateMemberLists(source, fields) {
  const lists = [];
  if (!source || typeof source !== 'object') {
    return lists;
  }
  (fields || MEMBER_LIST_FIELDS).forEach((field) => {
    const value = source[field];
    if (Array.isArray(value)) {
      lists.push(value);
    }
  });
  return lists;
}

function isDeclaredValue(value) {
  if (value === true) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    const normalised = value.trim().toLowerCase();
    return normalised === 'yes' || normalised === 'true';
  }
  return false;
}

function anyConditionFlagSet(sources) {
  let declared = false;
  sources.forEach((source) => {
    if (!source || typeof source !== 'object') {
      return;
    }
    CONDITION_FLAG_FIELDS.forEach((field) => {
      if (isDeclaredValue(source[field])) {
        declared = true;
      }
    });
  });
  return declared;
}

/**
 * Every place a policy-level "additional details" blob can hide, flattened.
 *
 * Callers may pass a single object or an array of them — a policy carries one at policy
 * level and another on its quotation, and an amended policy's richer snapshot lands on
 * the quotation only, so a reader that looks at just one of them misses what was
 * captured (OMK individual retest, items 3 and 5).
 */
function additionalDetailSources(additionalDetails) {
  const blobs = Array.isArray(additionalDetails)
    ? additionalDetails
    : [additionalDetails];
  const sources = [];
  blobs.forEach((blob) => {
    if (!blob || typeof blob !== 'object') {
      return;
    }
    sources.push(blob);
    if (blob.quotation_data && typeof blob.quotation_data === 'object') {
      sources.push(blob.quotation_data);
    }
  });
  return sources;
}

/**
 * Conditions recorded once at policy/quotation level for every member, keyed
 * back to a specific person by id or name.
 *
 * Used as a fallback when the dependant record itself carries no conditions.
 * `additionalDetails` may be one blob or an array of them.
 */
export function findMedicalConditionsForPerson(additionalDetails, person) {
  if (!additionalDetails || !person) {
    return [];
  }
  const sources = additionalDetailSources(additionalDetails);
  const matches = [];
  sources.forEach((source) => {
    candidateMemberLists(source).forEach((list) => {
      list.forEach((record) => {
        if (record && typeof record === 'object' && isSamePerson(record, person)) {
          matches.push(record);
        }
      });
    });
  });
  return mergeConditions(matches);
}

/**
 * Whether this person appears in a policy level list that exists only to name
 * the members who declared something (e.g.
 * additional_details.members_with_preexisting_conditions). Entries there may be
 * bare name strings, in which case the listing is the whole declaration.
 */
export function isPersonListedWithCondition(additionalDetails, person) {
  if (!additionalDetails || !person) {
    return false;
  }
  const personName = normaliseForMatch(getFullName(person));
  const sources = additionalDetailSources(additionalDetails);
  let listed = false;
  sources.forEach((source) => {
    candidateMemberLists(source, CONDITION_MEMBER_LIST_FIELDS).forEach((list) => {
      list.forEach((record) => {
        if (typeof record === 'string') {
          if (personName && normaliseForMatch(record) === personName) {
            listed = true;
          }
          return;
        }
        if (record && typeof record === 'object' && isSamePerson(record, person)) {
          listed = true;
        }
      });
    });
  });
  return listed;
}

/**
 * Whether the policy itself records that SOMEONE declared a condition, without
 * saying who.
 *
 * Deliberately separate from isPersonListedWithCondition: a bare policy level
 * "any pre-existing condition? yes" names nobody, so it must not be read as a
 * declaration against a particular member — capping every life on the policy
 * off one unattributed flag would underpay valid claims. It is still worth
 * surfacing, because "nothing was declared" is then the wrong thing to tell an
 * assessor.
 */
export function isConditionDeclaredOnPolicy(additionalDetails) {
  if (!additionalDetails) {
    return false;
  }
  return anyConditionFlagSet(additionalDetailSources(additionalDetails));
}

/**
 * Every named condition for a member of a quotation's policy_schedule_entries,
 * looking at the dependant, the schedule entry, and then the policy level
 * fallback.
 */
export function getScheduleEntryMedicalConditions(entry, additionalDetails) {
  if (!entry || typeof entry !== 'object') {
    return [];
  }
  const dependant = entry.dependant || {};
  const direct = mergeConditions([
    dependant,
    dependant.additional_details,
    dependant.details,
    entry,
    entry.additional_details
  ]);
  if (direct.length) {
    return direct;
  }
  return findMedicalConditionsForPerson(additionalDetails, dependant);
}

/**
 * Whether a medical condition was declared for this member at all, even when
 * it was captured as a bare yes/no with no condition named.
 */
export function isMedicalConditionDeclaredForScheduleEntry(
  entry,
  additionalDetails
) {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  const dependant = entry.dependant || {};
  if (
    anyConditionFlagSet([
      dependant,
      dependant.additional_details,
      dependant.details,
      entry,
      entry.additional_details
    ])
  ) {
    return true;
  }
  return isPersonListedWithCondition(additionalDetails, dependant);
}

/**
 * Render a condition list for display.
 */
export function formatMedicalConditions(conditions, placeholder) {
  const fallback = placeholder === undefined ? 'None declared' : placeholder;
  if (!conditions || !conditions.length) {
    return fallback;
  }
  return conditions.join(', ');
}

/**
 * What to show against a member: the named conditions when they were captured,
 * otherwise a clear statement of whether anything was declared at all. The
 * three outcomes are deliberately distinct — "Diabetes", "declared but not
 * named", and "nothing declared" mean different things to an assessor.
 */
export function describeMedicalConditions(conditions, declared) {
  if (conditions && conditions.length) {
    return conditions.join(', ');
  }
  if (declared) {
    return 'Declared — condition not specified';
  }
  return 'None declared';
}

/**
 * describeMedicalConditions for a policy_schedule_entries member.
 */
export function describeScheduleEntryMedicalConditions(
  entry,
  additionalDetails
) {
  return describeMedicalConditions(
    getScheduleEntryMedicalConditions(entry, additionalDetails),
    isMedicalConditionDeclaredForScheduleEntry(entry, additionalDetails)
  );
}
